import { useCallback, useEffect, useMemo, useState } from "react"
import {
  ActivityIndicator,
  LayoutChangeEvent,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from "react-native"

import { router, useLocalSearchParams } from "expo-router"
import { usePostHog } from "posthog-react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { BrandBanner } from "src/components/BrandBanner"
import { CompositeInputCluster } from "src/components/CompositeInputCluster"
import {
  channelMediaItemToContentLibraryItem,
  ContentLibraryGrid,
  ContentLibraryItem,
  ContentLibrarySearchBar,
  ContentLibrarySortBar,
  LoginOrRequestAccessModal,
  searchItems,
  sortAndFilterItems,
  SortKey,
  UnauthActionButton,
} from "src/components/ContentLibrary"
import { UnauthAvatarOverlay } from "src/components/ContentLibrary/UnauthAvatarOverlay"
import { Text } from "src/components/Ignite/Text"
import LinearGradient from "src/components/LinearGradient"
import { useChannelLink } from "src/context/ChannelLinkContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useUnauthChannelInfoQuery } from "src/queries/useUnauthChannelInfo"
import { useUnauthChannelMedia } from "src/queries/useUnauthChannelMedia"
import { ThemedStyle } from "src/theme"
import { hexToRgba } from "src/utils/colorUtils"

export default function UnauthChannelLibraryScreen() {
  const { id: channelId } = useLocalSearchParams<{ id: string }>()
  const { themed, theme } = useAppTheme()
  const { colors } = theme
  const insets = useSafeAreaInsets()
  const posthog = usePostHog()
  const { setPendingChannelId } = useChannelLink()

  // Sync the path-based channel ID into ChannelLinkContext so that MobileAppBanner
  // can construct the correct deep link when the user lands here directly from a web
  // URL (e.g. https://app.sidekickplatform.com/unauth/channel-library/<id>) without
  // going through the ?channel= query-param flow that ChannelLinkCapture watches.
  useEffect(() => {
    if (channelId) setPendingChannelId(channelId)
  }, [channelId, setPendingChannelId])

  const { data: channelInfo, isLoading: isLoadingInfo } = useUnauthChannelInfoQuery(channelId)
  const {
    data: mediaItems = [],
    isLoading: isLoadingMedia,
    isError: isMediaError,
    error: mediaError,
  } = useUnauthChannelMedia(channelId)

  // Track screen view once data is loaded
  useEffect(() => {
    if (!isLoadingInfo && !isLoadingMedia) {
      posthog?.capture("unauth_cl_viewed", { channelId })
    }
  }, [isLoadingInfo, isLoadingMedia, channelId, posthog])

  // Sort / filter / search state
  const [activeSort, setActiveSort] = useState<SortKey>("latest")
  const [activeSidekick, setActiveSidekick] = useState<string | null>(null)
  const [activeTopic, setActiveTopic] = useState<string | null>(null)
  const [activeType, setActiveType] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchActive, setSearchActive] = useState(false)

  const handleSearchPress = useCallback(() => {
    setSearchActive(true)
    // Clear any active filters when entering search mode
    setActiveSort("latest")
    setActiveSidekick(null)
    setActiveTopic(null)
    setActiveType(null)
  }, [])
  const handleSearchCancel = useCallback(() => {
    setSearchQuery("")
    setSearchActive(false)
  }, [])

  // Lock/login modal
  const [modalVisible, setModalVisible] = useState(false)
  const [, setModalTrigger] = useState<"locked" | "interaction">("locked")

  const allItems = useMemo(() => mediaItems.map(channelMediaItemToContentLibraryItem), [mediaItems])

  const filteredItems = useMemo(() => {
    const sorted = sortAndFilterItems(allItems, activeSort, activeSidekick, activeTopic, activeType)
    return searchItems(sorted, searchQuery)
  }, [allItems, activeSort, activeSidekick, activeTopic, activeType, searchQuery])

  const handleSortChange = useCallback((sort: SortKey) => {
    setActiveSort(sort)
    setActiveSidekick(null)
    setActiveTopic(null)
    setActiveType(null)
    // Collapse search when a filter is selected
    setSearchActive(false)
    setSearchQuery("")
  }, [])

  const handleSubFilterChange = useCallback(
    (filter: { sidekick?: string | null; topic?: string | null; type?: string | null }) => {
      if (filter.sidekick !== undefined) setActiveSidekick(filter.sidekick)
      if (filter.topic !== undefined) setActiveTopic(filter.topic)
      if (filter.type !== undefined) setActiveType(filter.type)
    },
    [],
  )

  const handleSelectItem = useCallback(
    (item: ContentLibraryItem) => {
      posthog?.capture("unauth_cm_opened", {
        channelId,
        taskId: item.taskId ?? null,
        mediaType: item.mediaType ?? null,
      })

      router.push({
        pathname: "/unauth/media-viewer",
        params: {
          channelId,
          taskId: item.taskId,
          mediaType: item.mediaType ?? "unknown",
          url: item.mediaUrl ?? "",
          title: item.title,
          ...(item.thumbnailUrl ? { thumbnailUrl: item.thumbnailUrl } : {}),
        },
      } as any)
    },
    [channelId, posthog],
  )

  const handleSelectLockedItem = useCallback(
    (item: ContentLibraryItem) => {
      posthog?.capture("unauth_locked_cm_tapped", { channelId, taskId: item.taskId ?? null })
      setModalTrigger("locked")
      setModalVisible(true)
    },
    [channelId, posthog],
  )

  const handleTalkTypePress = useCallback(() => {
    setModalTrigger("interaction")
    setModalVisible(true)
  }, [])

  const handleLoginPress = useCallback(() => {
    router.replace("/unauth/login")
  }, [])

  const [bottomBarHeight, setBottomBarHeight] = useState(0)
  const handleBottomBarLayout = useCallback((e: LayoutChangeEvent) => {
    setBottomBarHeight(e.nativeEvent.layout.height)
  }, [])

  const isLoading = isLoadingInfo || isLoadingMedia

  if (isMediaError) {
    console.error("[UnauthChannelLibrary] Failed to load channel media:", mediaError)
  }
  const subscriptionUrl = channelInfo?.subscription_url ?? null
  const channelImageSource = channelInfo?.brand_image ? { uri: channelInfo.brand_image } : undefined
  const channelTitle = channelInfo?.brand_title ?? channelInfo?.name ?? undefined
  const channelSubtitle = channelInfo?.brand_subtitle ?? undefined

  return (
    <View style={[styled.container, { backgroundColor: colors.background }]}>
      {/* Header — brand pill + large page title */}
      <View style={[themed($header), { paddingTop: insets.top }]}>
        <View style={styled.headerBrandRow}>
          <View style={styled.headerBrandSpacer} />
          <View style={styled.headerBrandCenter}>
            <BrandBanner
              imageSource={channelImageSource}
              title={channelTitle}
              subtitle={channelSubtitle}
            />
          </View>
          <View style={styled.headerBrandSpacer} />
        </View>
        <Text style={themed($pageTitle)}>Content Library</Text>
      </View>

      {/* Sort bar with integrated search icon */}
      <ContentLibrarySortBar
        items={allItems}
        activeSort={activeSort}
        activeSidekick={activeSidekick}
        activeTopic={activeTopic}
        activeType={activeType}
        onSortChange={handleSortChange}
        onSubFilterChange={handleSubFilterChange}
        onSearchPress={handleSearchPress}
        searchActive={searchActive}
      />

      {/* Search bar — only visible when expanded */}
      {searchActive && (
        <ContentLibrarySearchBar
          query={searchQuery}
          onQueryChange={setSearchQuery}
          onCancel={handleSearchCancel}
        />
      )}

      {/* Grid */}
      {isLoading ? (
        <View style={styled.loadingContainer}>
          <ActivityIndicator size="large" color={colors.tint ?? colors.primary} />
        </View>
      ) : (
        <ContentLibraryGrid
          items={filteredItems}
          onSelectItem={handleSelectItem}
          onSelectLockedItem={handleSelectLockedItem}
          style={styled.grid}
          contentPaddingBottom={insets.bottom + 100}
        />
      )}

      {/* Gradient fade above the bottom bar — matches the MediaPlayerShell treatment. */}
      {bottomBarHeight > 0 && (
        <LinearGradient
          colors={[hexToRgba(colors.background, 0), hexToRgba(colors.background, 1)]}
          style={[styled.bottomGradient, { bottom: bottomBarHeight }]}
          pointerEvents="none"
        />
      )}

      {/* Bottom controls: Login on the left, Talk + Type pill on the right.
          Both action surfaces are styled to match the unauth media-viewer's
          bottom pill (which mirrors the authenticated ButtonCluster look). */}
      <View
        style={[
          themed($bottomBar),
          {
            paddingBottom: insets.bottom + 8,
            paddingLeft: insets.left + 16,
            paddingRight: insets.right + 16,
          },
        ]}
        onLayout={handleBottomBarLayout}
      >
        <UnauthActionButton icon="login" label="Login" onPress={handleLoginPress} />
        <CompositeInputCluster
          style={styled.bottomCluster}
          onInteractionBlocked={handleTalkTypePress}
        />
      </View>

      {/* Animated Rive avatar overlay — plays intro message on first visit, persists as minimized/tab */}
      <UnauthAvatarOverlay channelId={channelId} />

      {/* Login or Request Access modal */}
      <LoginOrRequestAccessModal
        visible={modalVisible}
        onDismiss={() => setModalVisible(false)}
        subscriptionUrl={subscriptionUrl}
        channelId={channelId}
      />
    </View>
  )
}

const styled = StyleSheet.create({
  container: {
    flex: 1,
  },
  bottomGradient: {
    position: "absolute",
    left: 0,
    right: 0,
    height: 32,
  },
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  grid: {
    flex: 1,
  },
  headerBrandRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
  },
  headerBrandSpacer: {
    width: 45,
  },
  headerBrandCenter: {
    flex: 1,
    alignItems: "center",
  },
  bottomCluster: {
    flex: 1,
  },
})

const $header: ThemedStyle<ViewStyle> = ({ colors }) => ({
  paddingBottom: 4,
  backgroundColor: colors.background,
})

const $pageTitle: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 28,
  fontFamily: typography.primary.bold,
  color: colors.text,
  paddingHorizontal: 16,
  paddingTop: 8,
  paddingBottom: 4,
})

const $bottomBar: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  paddingTop: 10,
  backgroundColor: colors.background,
})
